import { createContext, useContext } from "react";

/**
 * Issue #757 review round 2 (B1), built under #803's decision (Andreas,
 * 2026-09-23, comment on #757): "a redrawn screen replaces its page — its
 * own chrome, its own grid — rather than sitting inside the shipped
 * layout." `lib/chromelessRoutes.js`'s `isChromelessRoute(pathname)` covers
 * a route where ROUTE + FLAG is the whole truth — `/onboarding` and
 * `/coach-setup` each have exactly one composition behind their own flag,
 * and Layout.jsx keeps its own shipped `<header>` for both (see that
 * file's own comment: the shipped header IS the one top bar those two
 * drawings show, just restyled).
 *
 * `/performance` is not that shape. The SAME route serves the shipped
 * match-history page (Dialog-based scorecard, unaffected by this issue)
 * AND, behind `newUi("log-ground")` while a fresh "Log match" is mid-flow,
 * a fully redrawn step with its OWN navy `TopBar` — not the shipped white
 * one. A route-level answer can't tell those two apart; only the rendered
 * screen (`PerformanceTracking.jsx`'s `LogGroundFlow`) knows which one is
 * showing, the exact same "one route, two compositions" problem
 * `RetireTextSizeContext.js` already solves for `/coach-portal` — this is
 * that pattern's sibling, one layer up (the whole page frame, not just the
 * text-size control).
 *
 * `active: true` means the mounted screen supplies its OWN complete page:
 * Layout.jsx hides the sidebar, the mobile nav strip AND its own shared
 * `<header>` (never just the first two, unlike `isChromelessRoute`'s route
 * screens, which keep the shared header) — see `Layout.jsx`'s own comment
 * at the `<header>` conditional for exactly which two chromeless mechanisms
 * differ and why.
 *
 * THE DEFAULT VALUE'S SETTER IS A NO-OP, same reason
 * `RetireTextSizeContext.js` gives: a screen that renders outside
 * `<Layout/>` entirely, or that never calls `useChromelessOverride()`,
 * costs nothing.
 */
const ChromelessOverrideContext = createContext({ active: false, setActive: () => {} });

/** The rendered screen's own read/write handle — call `setActive(true)` on
 * mount and `setActive(false)` on unmount (a `useEffect` cleanup), never
 * conditionally re-called on every render, for the same reason
 * `RetireTextSizeContext.js`'s own `useRetireTextSize()` gives: `Layout.jsx`
 * holds this as ordinary `useState`, so two screens both leaving it `true`
 * on unmount would fight each other. */
export function useChromelessOverride() {
  return useContext(ChromelessOverrideContext);
}

export const ChromelessOverrideProvider = ChromelessOverrideContext.Provider;
